import { HttpParams } from "@angular/common/http";
import { Component, forwardRef, Inject, Input } from "@angular/core";
import { Value } from "../../model";
import { ColorManagerService } from "../../service/color-manager.service";
import { DataService } from "../../service/data.service";
import { AbstractThingContextService } from "../../shared/class/abstract-thing-context-service.class";
import { MetricData, MetricDetails } from "./daily-summary-grid-widget.component";

@Component({
    selector: 'daily-summary-grid-widget-metric-details',
    template: require('./daily-summary-grid-widget-metric-details.component.html'),
    styles: [require('./daily-summary-grid-widget-metric-details.component.css')]
})
export class DailySummaryGridWidgetMetricDetailsComponent {

    @Input() set data(data: { metricData: MetricData[], timestamp: number, metricDetailsArray: MetricDetails[] }) {
        this._data = data.metricData;
        this.timestamp = data.timestamp;
        this.metricDetailsArray = data.metricDetailsArray || [];
        this.computeTableData();
    }

    @Input() timezone: string;

    @Input() locale: string;

    tableData: TableDataElement[];
    _data: MetricData[];
    visibleColumns: string[];
    dataLoaded: boolean;
    highlightColor: string;
    metricDetailsArray: MetricDetails[];

    private timestamp: number;

    constructor(
        @Inject(forwardRef(() => ColorManagerService)) private colorManagerService: ColorManagerService,
        @Inject(forwardRef(() => DataService)) private dataService: DataService,
        @Inject(forwardRef(() => AbstractThingContextService)) private thingContextService: AbstractThingContextService
    ) {
        this.highlightColor = this.colorManagerService.getColor('CHART_NETURAL') || '#212121';
    }

    private computeTableData(): void {
        this.dataLoaded = false;
        if (this._data?.length) {
            let tableData: TableDataElement[] = [];
            this._data.forEach(data => {
                let tableDataElement = tableData.find(d => d.timestamp == data.timestamp);
                if (!tableDataElement) {
                    let dataMap: { [metric: string]: { value: any, filter: any, filterArg: any, highlightColor: string } } = {};
                    dataMap[data.metricLabel] = { value: data.value, filter: data.filter, filterArg: data.filterArg, highlightColor: data.highlightColor };
                    tableData.push({ timestamp: data.timestamp, data: dataMap, isPreviousValue: false });
                } else {
                    tableDataElement.data[data.metricLabel] = { value: data.value, filter: data.filter, filterArg: data.filterArg, highlightColor: data.highlightColor };
                }
            });
            tableData.sort((a, b) => a.timestamp - b.timestamp);
            this.visibleColumns = ['timestamp'].concat(this.metricDetailsArray.map(m => { return m.label }).sort());
            this.getPreviousValues(this.timestamp).then(previousValues => {
                this.tableData = (previousValues.concat(tableData));
                this.dataLoaded = true;
            });
        } else {
            this.dataLoaded = true;
            this.tableData = [];
        }
    }

    private getPreviousValues(timestamp: number): Promise<TableDataElement[]> {
        let promises = [];
        this.metricDetailsArray.forEach(m => {
            promises.push(this.dataService.getLastValueByThingIdAndMetricName(this.thingContextService.getCurrentThing().id, m.name, new HttpParams().set('endDate', timestamp - 1)).then(value => {
                return { metricLabel: m.label, value: value, filter: m.filter, filterArg: m.filterArg };
            }));
        });
        return Promise.all(promises).then((previousValueResults: { metricLabel: string, value: Value, filter: any, filterArg: any }[]) => {
            const validPreviousValues = previousValueResults.filter(previousValue => previousValue.value != null);
            if (validPreviousValues.length) {
                let previousDataElement: TableDataElement = { timestamp: timestamp, data: {}, isPreviousValue: true };
                validPreviousValues.forEach(previousValue => {
                    previousDataElement.data[previousValue.metricLabel] = { value: previousValue.value.value, filter: previousValue.filter, filterArg: previousValue.filterArg, highlightColor: null };
                });
                return [previousDataElement];
            } else {
                return [];
            }
        }).catch(() => { return [] });
    }
}

class TableDataElement {
    timestamp: number;
    data: { [metricLabel: string]: { value: any, filter: any, filterArg: any, highlightColor: string } };
    isPreviousValue: boolean;
}